import { usePostApiV1SelectionsJobInformation } from "@/api/recrutaAI/generated/aISaaSAPI";
import {
	Box,
	Button,
	Group,
	Modal,
	Stack,
	Text,
	Textarea,
	useMantineTheme,
} from "@mantine/core";
import { useForm, zodResolver } from "@mantine/form";
import { useEffect, useState } from "react";
import { notifications } from "@mantine/notifications";
import {
	type RecruitPositionGenerateForm,
	recruitPositionGenerateFormMethodSchema,
	recruitPositionGenerateFormSchema,
} from "../../schemas/recruitPositionGenerateForm";

const GENERATE_METHOD_OPTIONS = [
	{
		label: "WebサイトのURL",
		value: recruitPositionGenerateFormMethodSchema.enum.webSiteUrl,
		placeholder: "https://example.com/job-posting",
	},
	{
		label: "テキスト記入",
		value: recruitPositionGenerateFormMethodSchema.enum.textInput,
		placeholder: `職務内容
AIを用いた自動化プロダクトにおけるエンジニアとして事業を共に推進してくださる人材を募集しています
0→1フェーズにおける価値検証・技術検証から実際のプロダクト構築まで事業立ち上げに幅広く関わっていただきます
短期に顧客価値をデリバリーすることと中長期目線での持続的成長（技術負債のコントロールなどを含む）とのバランスを取りながら開発を実施いただきます
		`,
		placeholderTitle: "モバイルアプリエンジニア",
	},
] as const;

type Props = {
	opened: boolean;
	close: () => void;
	handleRefetch: () => void;
};

export function RecruitPositionGenerateModal({
	opened,
	close,
	handleRefetch,
}: Props) {
	const theme = useMantineTheme();
	const [isSubmitting, setIsSubmitting] = useState(false);
	
	// Reset isSubmitting when modal is closed
	useEffect(() => {
		if (!opened) {
			setIsSubmitting(false);
		}
	}, [opened]);

	const recruitPositionGenerateForm = useForm<RecruitPositionGenerateForm>({
		mode: "uncontrolled",
		initialValues: {
			method: recruitPositionGenerateFormMethodSchema.enum.textInput,
			title: "",
			textInput: "",
		},
		validate: zodResolver(recruitPositionGenerateFormSchema),
	});

	const { mutate: postApiV1SelectionsJobInformation } =
		usePostApiV1SelectionsJobInformation({
			mutation: {
				onSuccess: () => {
					setIsSubmitting(false);
					notifications.show({
						title: "選考基準の生成を開始しました",
						message: "選考基準の生成を開始しました。",
						color: "primary",
					});
					handleRefetch();
					recruitPositionGenerateForm.reset();
					close();
				},
				onError: () => {
					setIsSubmitting(false);
					notifications.show({
						title: "選考基準の生成に失敗しました",
						message: "選考基準の生成に失敗しました。",
						color: "red",
					});
				},
			},
		});

	const handleSubmit = (values: RecruitPositionGenerateForm) => {
		setIsSubmitting(true);
		const data =
			values.method === recruitPositionGenerateFormMethodSchema.enum.webSiteUrl
				? {
						name: "",
						url: values.webSiteUrl,
					}
				: {
						name: values.title,
						text: values.textInput,
					};

		postApiV1SelectionsJobInformation({
			data,
		});
	};

	return (
		<Modal.Root
			opened={opened}
			// biome-ignore lint/suspicious/noEmptyBlockStatements: <explanation>
			onClose={() => {
				setIsSubmitting(false);
			}}
			size="xl"
			radius="md"
			centered
		>
			<Modal.Overlay />
			<Modal.Content>
				<Modal.Header>
					<Modal.Title fz="xl">選考基準を生成</Modal.Title>
					<Modal.CloseButton
						onClick={() => {
							recruitPositionGenerateForm.reset();
							setIsSubmitting(false);
							close();
						}}
					/>
				</Modal.Header>
				<Modal.Body p="lg">
					<form
						onSubmit={recruitPositionGenerateForm.onSubmit(handleSubmit)}
					>
						<Stack gap="lg">
							<Stack>
								<Text>
									求人票の内容をテキストで入力してください。
									<br />
									AIがその情報をもとに選考基準を生成します。
								</Text>
								<Stack>
									{/* <Radio.Group
										name="generateMethod"
										label="生成方法"
										withAsterisk
										key={recruitPositionGenerateForm.key("method")}
										{...recruitPositionGenerateForm.getInputProps("method")}
									>
										<Group mt="xs">
											{GENERATE_METHOD_OPTIONS.map((option) => (
												<Radio
													key={option.value}
													value={option.value}
													label={option.label}
												/>
											))}
										</Group>
									</Radio.Group> */}
									{recruitPositionGenerateForm.getValues().method ===
									recruitPositionGenerateFormMethodSchema.enum.webSiteUrl ? (
										<>
											<Textarea
												placeholder={
													GENERATE_METHOD_OPTIONS.find(
														(option) =>
															option.value ===
															recruitPositionGenerateFormMethodSchema.enum
																.webSiteUrl,
													)?.placeholder
												}
												key={recruitPositionGenerateForm.key("webSiteUrl")}
												autosize
												minRows={1}
												{...recruitPositionGenerateForm.getInputProps(
													"webSiteUrl",
												)}
											/>
											<Text>
												以下のような求人ページのURLを入力してください
												<br />
												<Text span>
													例）
													<a
														target="_blank"
														href="https://jobs.algomatic.jp/11b9a86da301804fb6dfde8d4756fe12"
														rel="noreferrer"
														style={{
															textDecoration: "none",
															color: theme.colors.primary[6],
														}}
													>
														https://jobs.algomatic.jp/11b9a86da301804fb6dfde8d4756fe12
													</a>
												</Text>
											</Text>
										</>
									) : (
										<Box>
											<Text fz="sm">
												選考基準名(求人名){" "}
												<span style={{ color: "red" }}>*</span>
											</Text>
											<Textarea
												placeholder={
													GENERATE_METHOD_OPTIONS.find(
														(option) =>
															option.value ===
															recruitPositionGenerateFormMethodSchema.enum
																.textInput,
													)?.placeholderTitle
												}
												key={recruitPositionGenerateForm.key("title")}
												autosize
												minRows={1}
												{...recruitPositionGenerateForm.getInputProps("title")}
											/>
											<Text fz="sm">
												求人内容 <span style={{ color: "red" }}>*</span>
											</Text>
											<Textarea
												placeholder={
													GENERATE_METHOD_OPTIONS.find(
														(option) =>
															option.value ===
															recruitPositionGenerateFormMethodSchema.enum
																.textInput,
													)?.placeholder
												}
												key={recruitPositionGenerateForm.key("textInput")}
												autosize
												minRows={7}
												{...recruitPositionGenerateForm.getInputProps(
													"textInput",
												)}
											/>
											<Text fz="xs" c="dimmed">
												100〜5000文字以内で入力してください
											</Text>
										</Box>
									)}
								</Stack>
							</Stack>
							<Group justify="flex-end">
								<Button
									type="submit"
									disabled={!recruitPositionGenerateForm.isDirty() || isSubmitting}
									loading={isSubmitting}
								>
									<Text>生成を開始</Text>
								</Button>
							</Group>
						</Stack>
					</form>
				</Modal.Body>
			</Modal.Content>
		</Modal.Root>
	);
}
